import { z } from 'zod';
import { UserRole } from '../enums';
import { paginatedResponse } from './common';

/**
 * Schemas for the /users admin endpoints.
 * Mirrored on client and server via @kreart/shared.
 */

const baseUser = z.object({
  id: z.string().cuid(),
  email: z.string().email(),
  phone: z.string().nullable(),
  fullName: z.string(),
  role: z.nativeEnum(UserRole),
  active: z.boolean(),
  mustChangePassword: z.boolean(),
  lastLoginAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  deletedAt: z.string().datetime().nullable(),
});

export const userOfferStats = z.object({
  totalOffers: z.number().int().nonnegative(),
  acceptedOffers: z.number().int().nonnegative(),
  rejectedOffers: z.number().int().nonnegative(),
  pendingOffers: z.number().int().nonnegative(),
  conversionRate: z.number().int().min(0).max(100),
});
export type UserOfferStats = z.infer<typeof userOfferStats>;

export const usersListSummary = z.object({
  totalUsers: z.number().int().nonnegative(),
  totalOffers: z.number().int().nonnegative(),
  acceptedOffers: z.number().int().nonnegative(),
  averageConversionRate: z.number().int().min(0).max(100),
});
export type UsersListSummary = z.infer<typeof usersListSummary>;

export const userListItem = baseUser.extend({
  offerStats: userOfferStats,
});
export type UserListItem = z.infer<typeof userListItem>;

export const userDetails = baseUser.extend({
  updatedAt: z.string().datetime(),
});
export type UserDetails = z.infer<typeof userDetails>;

export const listUsersQuery = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
  search: z.string().trim().max(100).optional(),
  role: z.nativeEnum(UserRole).optional(),
  active: z
    .union([z.boolean(), z.enum(['true', 'false'])])
    .transform((v) => (typeof v === 'string' ? v === 'true' : v))
    .optional(),
  includeDeleted: z
    .union([z.boolean(), z.enum(['true', 'false'])])
    .transform((v) => (typeof v === 'string' ? v === 'true' : v))
    .default(false),
});
export type ListUsersQuery = z.infer<typeof listUsersQuery>;

export const listUsersResponse = paginatedResponse(userListItem).extend({
  summary: usersListSummary,
});
export type ListUsersResponse = z.infer<typeof listUsersResponse>;

const optionalTrim = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .optional()
    .transform((v) => (v === '' ? undefined : v));

export const createUserRequest = z.object({
  email: z.string().email().toLowerCase().trim().max(254),
  phone: optionalTrim(40),
  fullName: z.string().trim().min(1).max(120),
  role: z.nativeEnum(UserRole),
});
export type CreateUserRequest = z.infer<typeof createUserRequest>;

export const updateUserRequest = z
  .object({
    phone: optionalTrim(40),
    fullName: z.string().trim().min(1).max(120).optional(),
    role: z.nativeEnum(UserRole).optional(),
    active: z.boolean().optional(),
  })
  .refine(
    (v) =>
      v.phone !== undefined ||
      v.fullName !== undefined ||
      v.role !== undefined ||
      v.active !== undefined,
    {
      message: 'At least one field is required.',
    },
  );
export type UpdateUserRequest = z.infer<typeof updateUserRequest>;

/**
 * The temporary password is returned ONCE in the create/reset response.
 * It is NEVER stored, logged, or written to the audit metadata.
 */
export const createUserResponse = userDetails.extend({
  temporaryPassword: z.string(),
});
export type CreateUserResponse = z.infer<typeof createUserResponse>;

export const resetPasswordResponse = z.object({
  temporaryPassword: z.string(),
});
export type ResetPasswordResponse = z.infer<typeof resetPasswordResponse>;
